/**
 * Money primitives.
 *
 * Two rules the rest of the codebase depends on:
 *
 *   1. A JS `number` never represents a monetary value. The exported types are
 *      `string`, so passing a number is a type error rather than a rounding bug
 *      discovered six months later on a client invoice.
 *   2. Rounding happens once, at the boundary. Intermediates carry 20 decimal
 *      places; a chain of `money()` calls inside one calculation compounds error.
 */
import Big from "big.js";

Big.DP = 20; // intermediate precision for division
Big.RM = Big.roundHalfUp; // invoices round the way people reading them expect
Big.NE = -21; // never fall back to exponential notation
Big.PE = 40;

/** A 2dp decimal string, e.g. `"40000.00"`. Never a number. */
export type Money = string;

/** A 6dp decimal string, e.g. `"0.085"` = 8.5%. Never a number. */
export type Rate = string;

export const ZERO: Money = "0.00";
export const ZERO_RATE: Rate = "0.000000";

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MoneyError";
  }
}

/** Accepts only decimal strings or Big instances — never `number`. */
type Numeric = Money | Rate | Big;

function toBig(v: Numeric, label = "value"): Big {
  if (v instanceof Big) return v;
  try {
    return new Big(v);
  } catch {
    throw new MoneyError(`${label} is not a valid decimal string: ${JSON.stringify(v)}`);
  }
}

/** Round to 2dp, half-up. The only place amounts become canonical. */
export function money(v: Numeric): Money {
  return toBig(v, "amount").round(2, Big.roundHalfUp).toFixed(2);
}

/** Round to 6dp, half-up. For percentages and weights. */
export function rate(v: Numeric): Rate {
  return toBig(v, "rate").round(6, Big.roundHalfUp).toFixed(6);
}

export function add(a: Money, b: Money): Money {
  return money(toBig(a).plus(toBig(b)));
}

export function sub(a: Money, b: Money): Money {
  return money(toBig(a).minus(toBig(b)));
}

export function neg(a: Money): Money {
  return money(toBig(a).times(-1));
}

export function sum(xs: readonly Money[]): Money {
  return money(xs.reduce<Big>((acc, x) => acc.plus(toBig(x)), new Big(0)));
}

/** Apply a rate to an amount: `mul("40000.00", "0.085")` → `"3400.00"`. */
export function mul(amount: Money, r: Rate): Money {
  return money(toBig(amount, "amount").times(toBig(r, "rate")));
}

/** Multiply by a quantity expressed as a decimal string. */
export function mulQty(unitPrice: Money, quantity: Rate): Money {
  return money(toBig(unitPrice, "unitPrice").times(toBig(quantity, "quantity")));
}

/** `a / b` expressed as a Rate. Returns 0 when `b` is zero. */
export function ratio(a: Money, b: Money): Rate {
  const denom = toBig(b);
  if (denom.eq(0)) return ZERO_RATE;
  return rate(toBig(a).div(denom));
}

/** `a / b`, rounded to 2dp. For margin-basis pricing: `cost / (1 - margin)`.
 *  Throws on division by zero rather than returning a silent zero — a caller
 *  dividing by a live denominator has a bug, not a zero to report. */
export function div(a: Money, b: Money): Money {
  const denom = toBig(b, "divisor");
  if (denom.eq(0)) throw new MoneyError("div: division by zero");
  return money(toBig(a, "dividend").div(denom));
}

/** `1 - r`, as a Rate. What's left after a rate is taken out of the whole —
 *  the denominator a margin-basis price divides by. */
export function complementRate(r: Rate): Rate {
  return rate(new Big(1).minus(toBig(r, "rate")));
}

export function sumRates(xs: readonly Rate[]): Rate {
  return rate(xs.reduce<Big>((acc, x) => acc.plus(toBig(x, "rate")), new Big(0)));
}

/* ---------------------------------------------------------------- *
 * Rates at the edge
 *
 * Rates are stored 0–1 because that is what they are multiplied by, and every
 * calculation in this package expects that form. People do not read or write
 * them that way: nobody says "a nought-point-one retainage". These three are
 * the only place the two forms meet — a screen or a form field — and they go
 * through Big rather than `n * 100` so a 6dp rate cannot pick up a float
 * artefact on its way to a page.
 * ---------------------------------------------------------------- */

/** `"0.085"` → `"8.5"`. Trailing zeros are dropped: this is for reading. */
export function rateToPercent(r: Rate): string {
  return toBig(r, "rate").times(100).toFixed();
}

/** `"8.5"` → `"0.085000"`. What a form field hands back to the API. */
export function percentToRate(percent: string): Rate {
  return rate(toBig(percent, "percent").div(100));
}

/** `"0.085"` → `"8.5%"`. */
export function formatPercent(r: Rate): string {
  return `${rateToPercent(r)}%`;
}

export function cmp(a: Money, b: Money): -1 | 0 | 1 {
  return toBig(a).cmp(toBig(b)) as -1 | 0 | 1;
}

export function eq(a: Money, b: Money): boolean {
  return toBig(a).eq(toBig(b));
}

export function isZero(a: Money): boolean {
  return toBig(a).eq(0);
}

export function isZeroRate(r: Rate): boolean {
  return toBig(r, "rate").eq(0);
}

export function isNegative(a: Money): boolean {
  return toBig(a).lt(0);
}

export function isPositive(a: Money): boolean {
  return toBig(a).gt(0);
}

export function maxMoney(a: Money, b: Money): Money {
  return toBig(a).gte(toBig(b)) ? money(a) : money(b);
}

export function minMoney(a: Money, b: Money): Money {
  return toBig(a).lte(toBig(b)) ? money(a) : money(b);
}

/**
 * Split `total` across `weights`, giving the last element the residual.
 *
 * This is the reconciliation rule the whole system leans on: milestone
 * resolution, overhead allocation, and any pro-rata split go through here, so
 * `sum(result) === total` exactly — never `99999.99` on a `100000.00` contract.
 *
 * Throws when the weights sum to zero. That is a real decision (equal split?
 * skip entirely?) and it belongs to the caller, not to a silent default here.
 */
export function splitByWeights(total: Money, weights: readonly Rate[]): Money[] {
  if (weights.length === 0) return [];
  if (weights.length === 1) return [money(total)];

  const weightTotal = weights.reduce<Big>((acc, w) => acc.plus(toBig(w, "weight")), new Big(0));
  if (weightTotal.eq(0)) {
    throw new MoneyError(
      "splitByWeights: weights sum to zero — the caller must choose a fallback basis",
    );
  }

  const t = toBig(total, "total");
  const out: Money[] = [];
  for (let i = 0; i < weights.length - 1; i++) {
    out.push(money(t.times(toBig(weights[i]!, "weight")).div(weightTotal)));
  }
  out.push(sub(total, sum(out)));
  return out;
}

/** Equal split with the residual on the last element. */
export function splitEvenly(total: Money, parts: number): Money[] {
  if (parts <= 0) return [];
  return splitByWeights(
    total,
    Array.from({ length: parts }, () => "1"),
  );
}

/** Split proportionally to a set of amounts (rather than explicit weights). */
export function splitByAmounts(total: Money, amounts: readonly Money[]): Money[] {
  return splitByWeights(
    total,
    amounts.map((a) => rate(toBig(a))),
  );
}
